import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import * as userActions from "../../redux/actions/userActions";
import APIConstant from "../../utility/APIConstant";
import { getActiveClientId } from "../../utility/ClientHelper";
import "./SdxCourses.scss";

/**
 * seconds -> "54s" / "15m" / "1h 5m"
 * The saga normalises both sources to seconds: getcourses already reports
 * seconds, getsdxstgcourses reports minutes and is multiplied up.
 */
const readDuration = (secs) => {
  const s = Number(secs);
  if (!s || s < 0) return "--";
  if (s < 60) return `${Math.round(s)}s`;

  const mins = Math.round(s / 60);
  if (mins < 60) return `${mins}m`;

  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
};

/** wraps the matched run so it is obvious why a row is in the results */
const highlight = (text, q) => {
  if (!q) return text;
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0) return text;
  return (
    <>
      {text.slice(0, i)}
      <mark>{text.slice(i, i + q.length)}</mark>
      {text.slice(i + q.length)}
    </>
  );
};

const SdxCourses = () => {
  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const userState = useSelector((state) => state.user);
  const { rows, loading, error, fetched } = userState.sdxCourses;
  const clientsList = useSelector((state) => state.client);

  const [query, setQuery] = useState("");
  // celms_course_id values ticked for the bulk update
  const [picked, setPicked] = useState(() => new Set());
  // sl is the natural order - the order SDX created the courses in
  const [sort, setSort] = useState({ key: "sl", dir: "asc" });

  useEffect(() => {
    if (!fetched) dispatch(userActions.sdxCoursesRequest());
  }, [fetched, dispatch]);

  // a reload can drop rows, so never keep a selection that no longer exists
  useEffect(() => {
    setPicked((prev) => {
      if (!prev.size) return prev;
      const live = new Set(rows.map((r) => r.celms_course_id));
      const next = new Set([...prev].filter((id) => live.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [rows]);

  const activeClientId = useMemo(
    () => getActiveClientId(userState, clientsList),
    [userState, clientsList]
  );

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const hits = q
      ? rows.filter(
          (r) =>
            r.course_name.toLowerCase().includes(q) ||
            String(r.celms_course_id).includes(q)
        )
      : rows;

    const sign = sort.dir === "asc" ? 1 : -1;
    return [...hits].sort((a, b) => {
      if (sort.key === "course_name") {
        return sign * a.course_name.localeCompare(b.course_name);
      }
      // sl and duration are both numeric; sl ties never happen, duration does,
      // so fall back to sl to keep equal durations in a predictable order
      const diff = (a[sort.key] || 0) - (b[sort.key] || 0);
      return sign * (diff || a.sl - b.sl);
    });
  }, [rows, query, sort]);

  const sortBy = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "asc" }
    );

  const arrow = (key) =>
    sort.key !== key ? "↕" : sort.dir === "asc" ? "↑" : "↓";

  const ariaSort = (key) =>
    sort.key !== key ? "none" : sort.dir === "asc" ? "ascending" : "descending";

  const togglePick = (id) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // select all covers what the search is currently showing, not the whole list
  const allShown = visible.length > 0 && visible.every((r) => picked.has(r.celms_course_id));
  const someShown = !allShown && visible.some((r) => picked.has(r.celms_course_id));

  const toggleAll = () =>
    setPicked((prev) => {
      const next = new Set(prev);
      visible.forEach((r) =>
        allShown ? next.delete(r.celms_course_id) : next.add(r.celms_course_id)
      );
      return next;
    });

  const progress = userState.sdxBulkProgress || { running: false, done: 0, total: 0 };

  const sendUpdate = () => {
    if (progress.running) return;
    const chosen = rows.filter((r) => picked.has(r.celms_course_id));
    if (!chosen.length) return;
    dispatch(userActions.sdxCoursesUpdateRequest({ rows: chosen }));
    setPicked(new Set());
  };

  // report the outcome of the bulk update
  const bulk = userState.sdxBulk;
  useEffect(() => {
    if (!bulk?.results?.length) return;
    const done = bulk.results.filter((r) => r.ok).length;
    const failed = bulk.results.length - done;

    addToast(
      failed
        ? `${done} of ${bulk.results.length} updated in SDX. ${failed} could not be sent: `
          + bulk.results.filter((r) => !r.ok).map((r) => r.name).join(", ")
        : `${done} course${done === 1 ? "" : "s"} updated in SDX`,
      {
        appearance: failed ? "error" : "success",
        autoDismiss: !failed,
        placement: "top-right",
      }
    );
  }, [bulk]);   // eslint-disable-line react-hooks/exhaustive-deps

  if (activeClientId && activeClientId !== APIConstant.SDX_CLIENT_ID) {
    return (
      <div className="sdx-courses">
        <div className="sdx-gate">
          SDX sync is available only for client {APIConstant.SDX_CLIENT_ID}.
          Switch the client from the header to view this list.
        </div>
      </div>
    );
  }

  return (
    <div className="sdx-courses">
      <div className="sdx-head">
        <div>
          <h2>SDX Courses</h2>
          <p>
            Every course created in Curator, and whether it has been moved to
            SDX staging.
          </p>
        </div>
        <button
          type="button"
          className="sdx-refresh"
          onClick={() => dispatch(userActions.sdxCoursesRequest())}
          disabled={loading || progress.running}
        >
          {loading ? "Refreshing..." : "Refresh"}
        </button>
      </div>

      <div className="sdx-card">
        <div className="sdx-toolbar">
          <div className="sdx-search">
            <i className="fa fa-search" aria-hidden="true" />
            <label className="sdx-sr" htmlFor="sdx-q">
              Search courses by name or course id
            </label>
            <input
              id="sdx-q"
              type="search"
              placeholder="Search course or id..."
              autoComplete="off"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Escape" && setQuery("")}
            />
            {query && (
              <button
                type="button"
                className="sdx-clear"
                onClick={() => setQuery("")}
                aria-label="Clear search"
              >
                &#10005;
              </button>
            )}
          </div>
        </div>

        {progress.running ? (
          <div className="sdx-selbar sdx-selbar-busy">
            <div className="spinner-border spinner-border-sm text-primary" role="status">
              <span className="sr-only">Updating...</span>
            </div>
            <b>
              Updating {progress.done} of {progress.total} in SDX...
            </b>
            <span className="sdx-sp" />
            <span className="sdx-progress" aria-hidden="true">
              <span
                className="sdx-progress-fill"
                style={{
                  width: `${progress.total ? (progress.done / progress.total) * 100 : 0}%`,
                }}
              />
            </span>
          </div>
        ) : picked.size > 0 ? (
          <div className="sdx-selbar">
            <b>{picked.size} selected</b>
            <span className="sdx-sp" />
            <button
              type="button"
              className="sdx-btn"
              onClick={() => setPicked(new Set())}
            >
              Clear
            </button>
            <button type="button" className="sdx-btn sdx-btn-go" onClick={sendUpdate}>
              Update {picked.size} in SDX
            </button>
          </div>
        ) : null}

        {loading && !rows.length ? (
          <div className="sdx-msg">Loading courses...</div>
        ) : error ? (
          <div className="sdx-msg sdx-msg-error">{error}</div>
        ) : !visible.length ? (
          <div className="sdx-msg">
            <b>
              {query
                ? `No course matches "${query}"`
                : "No courses in this view"}
            </b>
            {query && <span>Try the course name or its course id.</span>}
          </div>
        ) : (
          <div className="sdx-table-wrap">
            <table className="sdx-table">
              <thead>
                <tr>
                  <th className="sdx-sl" aria-sort={ariaSort("sl")}>
                    <button type="button" className="sdx-sort" onClick={() => sortBy("sl")}>
                      Sl No <span className="sdx-arrow">{arrow("sl")}</span>
                    </button>
                  </th>
                  <th aria-sort={ariaSort("course_name")}>
                    <button type="button" className="sdx-sort" onClick={() => sortBy("course_name")}>
                      Course <span className="sdx-arrow">{arrow("course_name")}</span>
                    </button>
                  </th>
                  <th className="sdx-dur" aria-sort={ariaSort("duration")}>
                    <button type="button" className="sdx-sort" onClick={() => sortBy("duration")}>
                      Duration <span className="sdx-arrow">{arrow("duration")}</span>
                    </button>
                  </th>
                  <th className="sdx-ck">
                    <input
                      type="checkbox"
                      checked={allShown}
                      ref={(el) => { if (el) el.indeterminate = someShown; }}
                      onChange={toggleAll}
                      disabled={progress.running}
                      aria-label="Select all shown courses"
                    />
                  </th>
                </tr>
              </thead>
              <tbody>
                {visible.map((r) => (
                  <tr
                    key={r.celms_course_id}
                    className={picked.has(r.celms_course_id) ? "on" : ""}
                  >
                    <td data-l="Sl No" className="sdx-sl">
                      {r.sl}
                    </td>
                    <td data-l="Course">
                      <div className="sdx-name">
                        {highlight(r.course_name, query.trim())}
                      </div>
                      <div className="sdx-sub">
                        id {highlight(String(r.celms_course_id), query.trim())}
                      </div>
                    </td>
                    <td data-l="Duration" className="sdx-dur">
                      {readDuration(r.duration)}
                    </td>
                    <td className="sdx-ck">
                      <input
                        type="checkbox"
                        checked={picked.has(r.celms_course_id)}
                        onChange={() => togglePick(r.celms_course_id)}
                        disabled={progress.running}
                        aria-label={`Select ${r.course_name}`}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="sdx-foot">
          {query
            ? `${visible.length} of ${rows.length} courses`
            : `${rows.length} courses`}
        </div>
      </div>
    </div>
  );
};

export default SdxCourses;
